import { useCallback, useEffect, useMemo, useState } from 'react'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { useSearchParams } from 'hooks'
import { useTranslate } from 'lib/tolgee'
import { useBrandTerritoryResolver } from 'modules/brands/common/hooks'
import { BRAND_CONTEXTS_BY_TYPE } from 'modules/brands/common/hooks/use-brand-niche-context-manager/use-brand-niche-context-manager.consts'
import { useNicheContextManager, useNicheWithContext, usePromotedNicheContext } from 'modules/niche/common/hooks'
import { NICHE_LIMIT, SYSTEM_CONTEXT_IDS } from 'modules/niche/common/niche.consts'
import { translations as nicheTranslations } from 'modules/niche/common/niche.i18n'
import type { INicheCard } from 'modules/niche/common/types'
import { analyticsTracker, humanizeLargeNumber, TOLGEE_GLOBALS } from 'utils'

import type { INicheChartsTab, INicheChartsTabBaseProps } from './niche-charts-tab.types'
import { getProviders } from '../common/niche.utils'
import { isIndustryCategoryTerritory } from '../common/promoted-niche.utils'

export const useNicheChartsTabModel = ({
  territoryId,
  brandType,
  feature,
  defaultUploadDate,
}: INicheChartsTabBaseProps): INicheChartsTab => {
  const [selectedNiches, setSelectedNiches] = useState<string[]>([])
  const [isInitialized, setIsInitialized] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const searchParams = useSearchParams()
  const initialContextId = searchParams.get('contextId') ?? undefined

  const isIndustryCategory = isIndustryCategoryTerritory(territoryId)

  const { getTerritoryIdForContext: brandTerritoryResolver, isReady: isBrandTerritoryReady } =
    useBrandTerritoryResolver({
      territoryId: territoryId,
      brandContentType: brandType,
    })
  const brandNicheContexts = brandType ? BRAND_CONTEXTS_BY_TYPE[brandType] : undefined

  const {
    excludeContextId,
    deferAutoSelect,
    scopedCategoryId,
    parentTerritoryIdsOverride,
    territoryContentFilters,
    territoryName,
  } = usePromotedNicheContext(territoryId)

  const {
    contexts,
    activeContext,
    handleActiveContext,
    error,
    handleRetrySelectContext,
    isLoadingContext,
    handleCreateContext,
    hasReachedLimit,
    isDefaultContext,
  } = useNicheContextManager({
    territoryId,
    initialContextId,
    customDefaultContexts: brandNicheContexts,
    territoryIdResolver: brandTerritoryResolver,
    readinessCheck: brandType ? { isReady: isBrandTerritoryReady } : undefined,
    deferAutoSelect,
    hideCreateContext: brandType === 'total',
    skipContextsQuery: brandType === 'total',
    skipDefaultContexts: isIndustryCategory,
    hideDelete: isIndustryCategory,
    featureType: feature,
    excludeContextId,
  })

  const { createToast } = useToast()
  const t = useTranslate()

  const hookProps = useMemo(() => {
    const providers = getProviders(searchParams)
    const uploadDateParam = searchParams.get('uploadDate') || defaultUploadDate
    return {
      contextHash: activeContext.hash,
      providers,
      territoryId,
      uploadDateParam,
      parentTerritoryIdsOverride,
      territoryContentFilters,
    }
  }, [
    searchParams,
    defaultUploadDate,
    activeContext.hash,
    territoryId,
    parentTerritoryIdsOverride,
    territoryContentFilters,
  ])

  const { niches, nicheStatus, unsubscribe, allNichesHaveErrors, handleRefetch, hasError, generatedNicheDescription } =
    useNicheWithContext(hookProps)

  const refetch = () => {
    if (error) {
      handleRetrySelectContext()
      return
    }
    handleRefetch()
  }

  const sortedNiches = useMemo(() => {
    return [...niches].sort((a, b) => (b?.totalEngagement ?? -1) - (a?.totalEngagement ?? -1))
  }, [niches])

  const firstNiches = useMemo(() => {
    return sortedNiches.slice(0, NICHE_LIMIT)
  }, [sortedNiches])

  const handleReset = () => {
    setSelectedNiches([])
  }

  const handleModal = () => {
    setIsModalOpen(prevState => !prevState)
  }

  const handleOpenDashboardModal = () => {
    const { TOP_OPEN_CREATE_REPORT } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(TOP_OPEN_CREATE_REPORT, {
      entityId: territoryId,
    })
    handleModal()
  }

  const handleNicheClick = useCallback(
    (id: string) => {
      const { TOGGLE_NICHE_VIEW } = analyticsTracker.eventNames
      analyticsTracker.eventTrack(TOGGLE_NICHE_VIEW, {
        nicheId: id,
        contextName: activeContext.name,
        contextId: activeContext.id,
        territoryTitle: territoryName,
        territoryId,
      })
      if (!selectedNiches.length) {
        const firstNichesId = firstNiches.map(item => item.id)
        if (!firstNichesId.includes(id)) {
          createToast({
            content: t(TOLGEE_GLOBALS.nicheLimitMessage(NICHE_LIMIT)),
          })
          return
        }
        setSelectedNiches(() => firstNichesId.filter(item => item !== id))
        return
      }
      if (selectedNiches.includes(id)) {
        if (selectedNiches.length === 1) {
          createToast({
            content: t(nicheTranslations.minNiche),
          })
          return
        }
        setSelectedNiches(prevState => prevState.filter(item => item !== id))
        return
      }
      if (selectedNiches.length >= NICHE_LIMIT) {
        createToast({
          content: t(TOLGEE_GLOBALS.nicheLimitMessage(NICHE_LIMIT)),
        })
        return
      }
      setSelectedNiches(prevState => [...prevState, id])
    },
    [activeContext.id, activeContext.name, createToast, territoryName, territoryId, firstNiches, selectedNiches, t]
  )

  const formattedNiches: INicheCard[] = useMemo(() => {
    const baseNiches = sortedNiches.map((item, index) => {
      const isDefaultSelected = !selectedNiches.length && index < NICHE_LIMIT

      return {
        name: item?.name,
        engagement: Math.max(0, item?.totalEngagement ?? 0),
        territoryId: item?.id,
        id: item?.id?.split('.')?.[1] || '',
        isLoading: item?.isLoading,
        isVisible: selectedNiches.includes(item.id) || isDefaultSelected,
        isClickDisabled: selectedNiches.length >= NICHE_LIMIT,
        handleClick: handleNicheClick,
        territoryQuery: item?.territoryQuery,
        parentTerritoryIds: item?.parentTerritoryIds,
        canCopyTerritoryQuery: isIndustryCategory,
        isPromoted: item?.isPromoted ?? null,
        promotedTargetId: item?.promotedTargetId ?? null,
        scopedCategoryId,
      }
    })
    return baseNiches
  }, [handleNicheClick, isIndustryCategory, scopedCategoryId, selectedNiches, sortedNiches])

  useEffect(() => {
    setIsInitialized(false)
    setSelectedNiches([])
  }, [activeContext.hash])

  useEffect(() => {
    if (nicheStatus) {
      setIsInitialized(true)
    }
  }, [nicheStatus])

  useEffect(() => {
    if (['completed', 'error'].includes(nicheStatus) && niches?.length) {
      unsubscribe()
    }
  }, [nicheStatus, unsubscribe, niches?.length])

  const loading = useMemo(() => {
    return !isInitialized || !nicheStatus
  }, [isInitialized, nicheStatus])

  const isNichePending = nicheStatus === 'pending'

  const isEmpty = useMemo(() => {
    return nicheStatus === 'empty' || (!niches.length && nicheStatus === 'completed')
  }, [nicheStatus, niches])

  const selectedItems = useMemo(() => {
    return selectedNiches.length > 0
      ? sortedNiches.filter(niche => selectedNiches.includes(niche.id)).slice(0, NICHE_LIMIT)
      : firstNiches
  }, [firstNiches, selectedNiches, sortedNiches])

  const territoryIds = useMemo(() => selectedItems.map(item => item.id), [selectedItems])

  const isChartReady = useMemo(() => {
    return (
      niches.every(niche => !niche.isLoading && niche.totalEngagement >= 0) &&
      niches.length &&
      nicheStatus === 'completed'
    )
  }, [nicheStatus, niches])

  const nichesWithPerformance = useMemo(() => {
    return selectedItems.map(item => ({
      id: item.id,
      name: item.name,
      totalViews: Math.max(0, item.totalViews ?? 0),
      totalEngagement: Math.max(0, item.totalEngagement ?? 0),
      totalPosts: Math.max(0, item.totalPosts ?? 0),
    }))
  }, [selectedItems])

  const territoryTitle = useMemo(() => {
    return territoryName || ''
  }, [territoryName])

  const chartUniverseData = useMemo(() => {
    const totalValues = nichesWithPerformance.reduce(
      (prev, curr) => {
        return {
          totalEngagement: prev.totalEngagement + curr.totalEngagement,
          totalPosts: prev.totalPosts + curr.totalPosts,
          totalViews: prev.totalViews + curr.totalViews,
        }
      },
      {
        totalEngagement: 0,
        totalPosts: 0,
        totalViews: 0,
      }
    )
    return `
      ${humanizeLargeNumber(totalValues.totalEngagement, 2)} ${t(TOLGEE_GLOBALS.engagement)},
      ${humanizeLargeNumber(totalValues.totalPosts, 2)} ${t(TOLGEE_GLOBALS.videos)},
      ${humanizeLargeNumber(totalValues.totalViews, 2)} ${t(TOLGEE_GLOBALS.views)}
    `
  }, [nichesWithPerformance, t])

  const isHappeningNow = useMemo(() => {
    return activeContext.id === SYSTEM_CONTEXT_IDS.happeningNow
  }, [activeContext.id])
  const hideCreateContext = isIndustryCategory || brandType === 'total'
  const shouldDisplayResetButton = selectedNiches.length > 0

  const shouldDisplayContextDescription = useMemo(() => {
    const isGeneratedAndEmpty = nicheStatus === 'completed' && !generatedNicheDescription
    return !isDefaultContext && !isGeneratedAndEmpty && !isHappeningNow && !isIndustryCategory
  }, [generatedNicheDescription, isDefaultContext, isHappeningNow, isIndustryCategory, nicheStatus])

  const isContextEmpty = !isLoadingContext && !contexts.length

  const hasReachedSelectedNicheLimit = !selectedNiches.length || selectedNiches.length >= NICHE_LIMIT

  const chartTotalsError = Boolean(allNichesHaveErrors || hasError || error)

  return {
    feature,
    contexts,
    activeContext,
    handleSelectContext: handleActiveContext,
    nicheList: formattedNiches,
    error: chartTotalsError,
    refetch: refetch,
    loading,
    isEmpty,
    territoryIds,
    territoryContentFilters,
    isChartReady,
    nichesWithPerformance,
    chartDataLoading: !isChartReady && !chartTotalsError,
    chartDataError: chartTotalsError,
    chartDataRefetch: refetch,
    isModalOpen,
    handleModal,
    territoryTitle,
    chartUniverseData,
    handleOpenDashboardModal,
    entityId: territoryId,
    isLoadingContext,
    hideCreateContext,
    handleCreateContext,
    hasReachedLimit,
    handleReset,
    shouldDisplayResetButton,
    shouldDisplayContextDescription,
    contextDescription: generatedNicheDescription,
    isContextEmpty,
    hasReachedSelectedNicheLimit,
    isNichePending,
    defaultUploadDate,
  }
}
